import { Auth, Principal } from "@codeArt/auth";
import React, { useEffect, useMemo, useState } from "react";
import { changed, createIcon } from "@codeArt/components/common";
import Tooltip from "@codeArt/components/Tooltip";
import MaterialButton from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { ListItemIcon } from "@material-ui/core";
import { type } from "@codeArt/utils/common";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  getThemeColor,
  getThemeColorValue
} from "@codeArt/components/theme/color";
import { pipeline } from "@codeArt/utils/auth";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import clsx from "clsx";
import _ from "lodash";
import FloatButton from "@codeArt/components/button/float";
import AppContext from "@jumbo/components/contextProvider/AppContextProvider/AppContext";
import { useContext } from "react";

export { FloatButton };

const useStyles = makeStyles(theme => ({
  tools: ({ right }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: right ? "end" : "start",
    alignItems: "center"
  }),
  button: ({ width }) => ({
    color: "#fff",
    fontWeight: 400,
    width: width || "auto",
    minWidth:width || "64px",
    "-webkit-app-region": "no-drag", //放置按钮在顶部，electron让顶部按钮失效
    "-webkit-user-select": "none"
  }),
  iconButton: ({ color }) => ({
    color: "#fff",
    background: getThemeColorValue(color),
    paddingLeft: 10,
    paddingRight: 10,
    minWidth: "auto",
    "& .MuiButton-label > span": {
      marginLeft: 0,
      marginRight: 0
    },
    "&:hover": {
      background: getThemeColorValue(color)
    }
  }),
  primary:{
    background:props=>props.theme.ca.primary,
    color:props => props.theme.ca.primary_text,
    "&:hover": {
      background:props => props.theme.ca.primary,
    }
  }
}));

export const Tools = ({ children, className, right }) => {
  const { theme } = useContext(AppContext);
  const classes = useStyles({ right,theme });
  return <div className={clsx(classes.tools, className)}>{children}</div>;
};

const _Button = ({
  id,
  hidden,
  tooltip,
  disabled,
  size = "md",
  color = "primary",
  variant,
  className = "",
  icon,
  children,
  permission,
  role,
  dark,
  width,
  onClick,
  verifyMessage
}) => {
  
  const { theme } = useContext(AppContext);
  const classes = useStyles({ width,theme });
  const dispatch = useDispatch();
  tooltip = useMemo(() => parseTooltip(tooltip), [tooltip]);

  const _color = useMemo(
    () => {
      if(color=="primary" && !disabled) return classes.primary;

      return color ? getThemeColor(disabled ? "disabled" : color, "bg") : "";
    },
    [disabled, color]
  );

  const rawDisabled = disabled;
  function parseTooltip(t) {
    t = t || {};
    const ty = type(t);
    if (ty === "string") return { description: t, placement: "bottom" };
    if (ty === "array") return { description: t[0], placement: t[1] };
    if (!t.placement) t.placement = "bottom";
    return t;
  }

  const getTooltipDescription = () => {
    if (disabled && tooltip.disabled) return tooltip.disabled;
    if (verifyMessage) return verifyMessage;
    return tooltip.description;
  };

  const tooltipWrapper = content => {
    const description = getTooltipDescription();
    return Tooltip({
      description,
      placement: tooltip.placement,
      // content
      content: disabled && description ? <div>{content}</div> : content //diabled状态下，必须包裹<div>才能显示提示
    });
  };

  // const verify = () => {   权限验证不再内部指定，而是用一个权限环境来统一处理
  //   Principal.verify(
  //     { permission, role, dark, dispatch },
  //     {
  //       before: () => {
  //         setDisabled(true);
  //         setVerifyMessage("正在效验权限");
  //       },
  //       success: () => {
  //         setDisabled(rawDisabled);
  //         setVerifyMessage("");
  //       },
  //       error: p => {
  //         p = p || {};
  //         if (p.dark) {
  //           setHidden(true); //由于灰度原因无法访问
  //           return;
  //         }

  //         setDisabled(true);
  //         setVerifyMessage("无此权限");
  //       }
  //     }
  //   );
  // };

  /* const getSize = size => {
    switch (size) {
      case "small":
      case "sm":
        return "small";
      case "medium":
      case "md":
        return "medium";
      case "large":
      case "lg":
        return "large";
      default:
        return "medium";
    }
  }; */

  const _size = useMemo(() => getSize(size), [size]);
  const _iconClassName = useMemo(() => getIconClassName(size), [size]);

  const hasContent = children && children.length > 0;
  const _icon = createIcon(
    icon,
    `${hasContent ? `mr-1 ${_iconClassName}` : `${_iconClassName}`}`,
    {
      fontSize: _size
    }
  );

  // useEffect(() => {
  //   verify();
  // }, []);

  if (hidden) return null;

  return tooltipWrapper(
    <MaterialButton
      variant={variant || "contained"}
      size={_size}
      className={`${_color} ${classes.button} ${className}`}
      disabled={disabled}
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      {icon ? (
        <React.Fragment>
          {_icon}
          {children}
        </React.Fragment>
      ) : (
        children
      )}
    </MaterialButton>
  );
};

const Button = React.memo(_Button, (prevProps, nextProps) => {
  if (changed(prevProps, nextProps, ["children", "icon", "tooltip", "disabled", "onClick"]))
    return false;
  return true;
});

export default Button;

const _IconButon = ({ color, icon, onClick, className }) => {
  const { theme } = useContext(AppContext);
  const classes = useStyles({ color,theme });

  return (
    <MaterialButton
      className={clsx(classes.iconButton, className)}
      onClick={onClick}
      variant="contained"
      startIcon={icon}
    />
  );
};

const IconButon = React.memo(_IconButon, (prevProps, nextProps) => {
  if (changed(prevProps, nextProps, ["color", "icon", "tooltip"]))
    return false;
  return true;
});

export { IconButon };

export class ModalButton extends React.Component {
  render() {
    return <Button shadow={0} size={"md"} className="c-ml-2" {...this.props} />;
  }
}

class MoreItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // auth: "loading"
    };
  }

  componentDidMount() {
    //auth改动
    let my = this;
    let { data } = this.props;

    let { disabled, disabledTip } = data;
    if (disabled === true) {
      my.setState({
        disabled,
        disabledTip
      });
      return;
    }
  }

  tooltip(content) {
    let { tip, tipPlacement } = this.props;
    let { disabled, disabledTip } = this.state;
    return Tooltip({
      description: disabled ? disabledTip : tip,
      placement: tipPlacement,
      content: <div>{content}</div>
    });
  }

  renderLink(lable,className) {
    let { data, onClick } = this.props;
    let href = type(data.href) === "function" ? data.href() : data.href;
    let { disabled } = this.state;
    return (
      <MenuItem
        disabled={disabled}
        className={`c-px-4d5 ${className}`}
        onClick={e => {
          onClick(e);
        }}
      >
        <a className="c-a" href={href} target="_blank" rel="noreferrer">
          {data.icon ? (
            createIcon(data.icon, `mr-2 align-middle c-menu-icon`)
          ) : null}
          {lable}
        </a>
      </MenuItem>
    );
  }

  renderMain(lable,className) {
    let { data, onClick } = this.props;
    let { disabled } = this.state;
    return (
      <MenuItem
        disabled={disabled}
        className={`c-px-4d5 ${className}`}
        onClick={e => {
          onClick(e);
        }}
      >
        {data.icon ? (
          <ListItemIcon style={{ minWidth: "auto" }}>
            {createIcon(data.icon, `mr-2 c-align-middle c-menu-icon`)}
          </ListItemIcon>
        ) : null}
        {lable}
      </MenuItem>
    );
  }

  render() {
    let { data } = this.props;
    let lable = data.label || data.text;
    let isLink = data.href;
    let color = data.color;
    let className = color ? getThemeColor(color) : '';
    return this.tooltip(
      isLink ? this.renderLink(lable,className) : this.renderMain(lable,className)
    );
  }
}

MoreItem.defaultProps = {};

class More extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: undefined,
      menuState: false,
      current: null
    };
  }

  componentDidMount() {
    //auth改动
    let my = this;
    let { options } = this.props;

    pipeline(options, {
      before: () => {
        my.setState({
          disabled: true,
          disabledTip: "正在效验权限"
        });
      },
      success: options => {
        my.setState({
          disabled: false,
          disabledTip: "",
          options
        });
      },
      error: () => {
        my.setState({
          disabled: true,
          disabledTip: "无此权限"
        });
      }
    });
  }

  componentWillUnmount() {
    this.setState = (state, callback) => {
      return;
    };
  }

  getCurrent() {
    return this.state.current;
  }

  onSelect(e, option) {
    this._select_callback = ()=>{
      let result = true;
      if (option.onClick) result = option.onClick(e);
      if (result !== false) {
        //没有显示指定返回false，那么更改选项
        this.setState({
          current: option
        });
      }
    };
    this.handleClose();
  }

  onOptionSelect = event => {
    event.stopPropagation();
    if (this.props.onClick) this.props.onClick();
    this.setState({ menuState: true, anchorEl: event.currentTarget });
  };

  handleClose = (cb) => {
    this.setState({ menuState: false });
  };

  //一定要在handleExited处理单个点击事件，因为有可能点击事件里跳转页面，而mask没有关闭，导致bug了
  handleExited=()=>{
    if(this._select_callback) {
      this._select_callback();
      this._select_callback = null;
    }
  }

  _renderButton({ disabled }) {
    let disabledTip = this.state.disabledTip || this.props.disabledTip;
    disabled = disabled || this.state.disabled || this.props.disabled;
    let content = this.renderButton({ disabled });
    if (disabled && disabledTip) {
      return Tooltip({
        key: 0,
        description: disabledTip,
        placement: "top",
        content: <div>{content}</div>
      });
    }
    return content;
  }

  renderButton({ disabled }) {
    let { style, type, className, iconClassName } = this.props;
    return (
      <IconButton
        key={0}
        className={`icon-btn p-2 ${className}`}
        disabled={disabled}
        style={style}
        onClick={this.onOptionSelect}
      >
        {type === "vert" ? (
          <MoreVertIcon className={iconClassName} />
        ) : (
          <MoreHorizIcon className={iconClassName} />
        )}
        {/*   <i className={`zmdi zmdi-more-${type} ${iconClassName}`} /> */}
      </IconButton>
    );
  }

  _menuItem(option, index) {
    return (
      <MoreItem
        key={index}
        data={option}
        onClick={e => {
          if (option.href) this.handleClose();
          else this.onSelect(e, option);
        }}
      />
    );
  }

  renderMenuItem(option, index) {
    if (option.auth === false) return null;

    if (!option.help) {
      return this._menuItem(option, index);
    }
    return Tooltip({
      key: index,
      description: option.help,
      placement: "left",
      content: <div key={index}>{this._menuItem(option, index)}</div>
    });
  }

  menuAnchor() {
    const { anchorEl } = this.state;
    return { anchorEl };
  }

  render() {
    let { style, type, width, className, iconClassName, onClick } = this.props;
    const { menuState, anchorEl } = this.state;
    let options = this.props.options || this.state.options;
    let items = options
      .map((option, index) => this.renderMenuItem(option, index))
      .filter(t => t);
    return [
      this._renderButton({ disabled: items.length === 0 }),
      options && options.length > 0 ? (
        <Menu
          key={1}
          {...this.menuAnchor()}
          open={menuState}
          onClose={this.handleClose}
          onClick={e => {
            e.stopPropagation();
          }} //阻止默认事件，主要是为了在datagrid中，阻止点击按钮后，行也被选择了
          TransitionProps={{
            onExited:()=> this.handleExited()
          }}
          
          MenuListProps={{
            style: {
              width: width || "auto"
            }
          }}
        >
          {items}
        </Menu>
      ) : null
    ];
  }
}

More.defaultProps = {
  className: "",
  iconClassName: "c-icon-s3 m-text-muted"
};

//右键菜单，不需要按钮
export class ContextMenu extends More {

  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      mouseX: null,
      mouseY: null
    }
  }

  open(event) {
    let { disabled } = this.state;
    if (disabled) return;

    this.setState({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    }, () => {
      this.onOptionSelect(event);
    });
  }

  menuAnchor() {
    const { mouseX, mouseY } = this.state;
    return {
      anchorReference: "anchorPosition",
      anchorPosition:
        mouseY !== null && mouseX !== null
          ? { top: mouseY, left: mouseX }
          : undefined
    }
  }

  _renderButton() {
    return null;
  }
}

export let MoreVert = props => {
  return <More type="vert" {...props} />;
};

export class ActionGroup extends React.Component {
  constructor(props) {
    super(props);
  }

  main() {
    let { children, title, moreClassName, onClick } = this.props;
    if (type(children) !== "array") children = [children];
    children = _.flatten(children); //有时候会在ActionGroup的子节点里动态输出数组，所以需要减少一次嵌套
    children = _.filter(children,(c)=>c);
    let options = children
      .map(c => {
        let {
          onClick,
          label,
          color,
          icon,
          href,
          permission,
          disabled,
          disabledTip
        } = c.props;
        return {
          icon: icon,
          color,
          label,
          href,
          permission,
          disabled,
          disabledTip,
          onClick
        };
      })
      .filter(function (item) {
        return item != null;
      });
    return (
      <MoreVert title={title} onClick={onClick} className={moreClassName} options={options} />
    );
  }

  render() {
    let { className } = this.props;
    return <div className={className}>{this.main()}</div>;
  }
}

ActionGroup.defaultProps = {
  className: "",
  MoreclassName: "c-text-grey c-icon-s2 c-icon-md-s3"
};


export class OutlinedButton extends React.Component {

  constructor(props) {
    super(props);
  }

  render() {
    let { color, size, children, icon, leftIcon, rightIcon, iconClassName, className } = this.props;
    let p = { ...this.props };
    delete p.color; delete p.size; delete p.icon; delete p.className; delete p.rightIcon; delete p.leftIcon; delete p.iconClassName;
    delete p.disabledTip;

    let hasText = children && children.length > 0;
    const _size = getSize(size);
    leftIcon = icon || leftIcon;
    iconClassName = `${getIconClassName(size)} ${iconClassName}`;

    const _leftIcon = createIcon(leftIcon, `${hasText ? `mr-1 ${iconClassName}` : ``}`, { fontSize: _size, key: "leftIcon" });
    const _rightIcon = createIcon(rightIcon, `${hasText ? `ml-1 ${iconClassName}` : ``}`, { fontSize: _size, key: "rightIcon" });

    return (
      <MaterialButton variant="outlined" color={color} size={_size} className={`c-button c-font-weight-normal ${className}`} {...p}>
        {[_leftIcon, children, _rightIcon]}
      </MaterialButton>
    );
  }
}


OutlinedButton.defaultProps = {
  color: 'primary',
  size: "md",
  className: '',
  iconClassName: ''
}

export class OutlinedButtonMore extends More {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state
    }
  }
  renderChildren() {
    return this.props.children;
    /*     let { children, selectMode, options } = this.props;
        if (!selectMode) return children;
        let current = this.getCurrent();
    
        if (!current) {
          current = options && options.length > 0 ? options[0] : null;
        }
        if (current) return current.label || current.text; */
  }

  renderButton({ disabled }) {
    let { button, style, className, iconClassName, size } = this.props;
    return (
      <OutlinedButton key={"outlinedButton"} onClick={this.onOptionSelect} size={size} style={style} disabled={disabled} className={className} iconClassName={iconClassName} {...button}>
        {this.renderChildren()}
      </OutlinedButton>
    );
  }
}

OutlinedButtonMore.defaultProps = {
  disabledTip: "无此权限",
  size: "md",
}


function getIconClassName(size) {
  switch (size) {
    case "sm":
      return "c-icon-s0d5";
    case "md":
      return "c-icon-s1";
    case "lg":
      return "";
    default:
  }
  return "";
}

function getSize(size) {
  switch (size) {
    case "small":
    case "sm":
      return "small";
    case "medium":
    case "md":
      return "medium";
    case "large":
    case "lg":
      return "large";
    default:
      return "medium";
  }
};

